import React, {FC, useCallback, useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {AxiosResponse} from 'axios'
import {corporateApi} from '../../../../setup/axios/CorporateApi'
import {ApiResponse} from '../../../../shared/models/ApiModels'
import {showErrorToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {searchApplications} from '../../hr/application/Api'
import {HR_APPLICATION_PATH, IApplicationModel} from '../../hr/application/ApplicationModel'
import {ApplicationTable} from '../../hr/application/ApplicationTable'
import {ApplicationDetailModal} from '../../hr/application/ApplicationDetailModal'

const API_URL = (process.env.REACT_APP_API_URL || '/').replace(/\/?$/, '/')
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50]

interface IHrDashboardSummary {
  openJobCount: number
  applicationCount: number
  inProgressTrackingCount: number
  candidateCount: number
}

function getHrDashboardSummary(): Promise<AxiosResponse<ApiResponse<IHrDashboardSummary>>> {
  return corporateApi.get(`${API_URL}api/v1/hr-dashboard/summary`)
}

const StatCard: FC<{label: string; value?: number; icon: string; color: string}> = ({label, value, icon, color}) => (
  <div className='col-sm-6 col-xl-3'>
    <div className='card h-100'>
      <div className='card-body d-flex align-items-center gap-5'>
        <div className={`symbol symbol-55px symbol-circle`}>
          <span className={`symbol-label bg-light-${color}`}>
            <i className={`bi ${icon} fs-2x text-${color}`}></i>
          </span>
        </div>
        <div className='d-flex flex-column'>
          <span className='fs-2hx fw-bolder text-gray-900 lh-1'>
            {value === undefined ? <span className='spinner-border spinner-border-sm text-gray-400' /> : value}
          </span>
          <span className='text-muted fw-semibold mt-2'>{label}</span>
        </div>
      </div>
    </div>
  </div>
)

/** HR_SPECIAL rolündeki kullanıcıların dashboard'u */
const HrDashboardPage: FC = () => {
  const [summary, setSummary] = useState<IHrDashboardSummary | null>(null)
  const [applications, setApplications] = useState<IApplicationModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0])
  const [loading, setLoading] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)

  useEffect(() => {
    getHrDashboardSummary()
      .then(({data}) => setSummary(data.payload))
      .catch((error) => showErrorToast(apiErrorMessage(error, 'Özet bilgiler yüklenemedi')))
  }, [])

  const fetchApplications = useCallback(async (page: number, size: number) => {
    setLoading(true)
    try {
      // Son başvuru üstte
      const payload = (await searchApplications({}, page, size)).data.payload
      setApplications(payload.content)
      setTotalPages(payload.totalPages)
      setCurrentPage(page)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Başvurular yüklenemedi'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchApplications(0, pageSize)
  }, [pageSize, fetchApplications])

  return (
    <>
      <div className='row g-6 mb-6'>
        <StatCard label='Açık İlan' value={summary?.openJobCount} icon='bi-briefcase' color='primary' />
        <StatCard label='Yapılan Başvuru' value={summary?.applicationCount} icon='bi-inbox' color='info' />
        <StatCard
          label='Süreci Devam Eden Aday'
          value={summary?.inProgressTrackingCount}
          icon='bi-hourglass-split'
          color='warning'
        />
        <StatCard label='Havuzdaki Aday' value={summary?.candidateCount} icon='bi-people' color='success' />
      </div>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Son Başvurular</span>
            <span className='text-muted mt-1 fw-bold fs-7'>En yeni başvuru en üstte</span>
          </h3>
          <div className='card-toolbar'>
            <Link to={HR_APPLICATION_PATH} className='btn btn-sm btn-light-primary'>
              Tüm Başvurular
            </Link>
          </div>
        </div>
        <div className='card-body pt-0'>
          <ApplicationTable
            data={applications}
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={setPageSize}
            onPageChange={(page) => page >= 0 && page < totalPages && fetchApplications(page, pageSize)}
            onDetail={(application) => setDetailId(application.id || null)}
            loading={loading}
          />
        </div>
      </div>

      <ApplicationDetailModal applicationId={detailId} onClose={() => setDetailId(null)} />
    </>
  )
}

export {HrDashboardPage}
